import { useState } from "react";
import { http, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { User, ShieldCheck, MailCheck, KeyRound } from "lucide-react";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);

  const saveProfile = async () => {
    setSaving(true);
    try { const u = await http.put("/auth/profile", { name }).then((r) => r.data); setUser(u); toast.success("Profile updated"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const changePw = async () => {
    if (pw.length < 8) return toast.error("Password must be at least 8 characters.");
    if (pw !== pw2) return toast.error("Passwords do not match.");
    try { await http.put("/auth/profile", { password: pw }); toast.success("Password changed"); setPw(""); setPw2(""); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resendVerify = async () => {
    try { await http.post("/auth/resend-verification"); toast.success("Verification email sent"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="My Profile" subtitle="Manage your account details and security." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-6 text-center">
          <div className="w-20 h-20 rounded-full bg-[#0B132B] text-white flex items-center justify-center text-2xl font-bold mx-auto">{(user?.name || "U").slice(0, 2).toUpperCase()}</div>
          <h3 className="font-display font-semibold text-[#0B132B] mt-4">{user?.name}</h3>
          <p className="text-sm text-slate-500">{user?.email}</p>
          <div className="flex items-center justify-center gap-2 mt-3">
            <StatusBadge status={user?.role === "admin" ? "accepted" : "sent"} testId="profile-role" />
            <span className="text-xs text-slate-500 capitalize">{user?.role}</span>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100">
            {user?.email_verified ? (
              <div className="flex items-center justify-center gap-1.5 text-emerald-600 text-sm" data-testid="verified-badge"><MailCheck className="w-4 h-4" /> Email verified</div>
            ) : (
              <>
                <div className="text-sm text-amber-600 mb-2">Email not verified</div>
                <Button variant="outline" size="sm" onClick={resendVerify} data-testid="resend-verification">Resend verification</Button>
              </>
            )}
          </div>
        </Card>

        <Card className="p-6 lg:col-span-2 space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-4"><User className="w-4 h-4 text-[#D4AF37]" /><h3 className="font-display font-semibold text-[#0B132B]">Account Details</h3></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Full Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="profile-name-input" /></div>
              <div><Label>Email</Label><Input value={user?.email || ""} disabled className="mt-1.5 bg-slate-50" data-testid="profile-email-input" /></div>
            </div>
            <Button onClick={saveProfile} disabled={saving} className="mt-4 bg-[#0B132B] hover:bg-[#1C2541]" data-testid="save-profile-button">{saving ? "Saving…" : "Save Profile"}</Button>
          </div>

          <div className="pt-6 border-t border-slate-100">
            <div className="flex items-center gap-2 mb-4"><KeyRound className="w-4 h-4 text-[#D4AF37]" /><h3 className="font-display font-semibold text-[#0B132B]">Change Password</h3></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>New Password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Min 8 characters" className="mt-1.5" data-testid="profile-password-input" /></div>
              <div><Label>Confirm Password</Label><Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1.5" data-testid="profile-password-confirm" /></div>
            </div>
            <Button onClick={changePw} variant="outline" className="mt-4" data-testid="change-password-button"><ShieldCheck className="w-4 h-4 mr-1.5" /> Update Password</Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
